import { Injectable } from "@angular/core";
import { FormGroup } from "@angular/forms";
import _upperFirst from 'lodash-es/upperFirst';

import {
    ICheckboxFieldSpecs,
    IDateFieldSpecs,
    IFieldSpecs
} from "libraries/module/formbuilder/fields/interfaces/field-specs";
import { HelperService } from "app_sasha/shared/services/helper.service";
import {
    FIELD_CHECKBOX_LABELS,
    FIELD_DESCRIPTION_LABELS,
    FIELD_LABELS,
    TEMPLATE_LABELS
} from "app_sasha/shared/config/booking-wording";
import { DATE_FIELDS_CONFIG, validatorConfig } from "./config";
import { IValidator } from "libraries/module/formbuilder/forms/utils/validators/IValidator";
import * as moment from 'moment';
import { IDateRangeOptionConfig, OptionConfigTypes } from "../date-field-set/date-field-set.component";
import { BookingSearchParameters } from "libraries/interfaces/IBookingSearchParameters";
import { IDateFieldConfig } from "app_sasha/shared/interfaces/IDateFieldConfig";
import { INumberFieldSpecs } from "libraries/module/formbuilder/fields/number/INumberFieldSpecs";

interface IFieldConfig {
    [name: string]: IFieldSpecs;
}

const DEFAULT_ACTIVE_DATE_FIELDS: BookingSearchParameters[] = [
    BookingSearchParameters.whenCreated,
    BookingSearchParameters.begin,
    BookingSearchParameters.end,
    BookingSearchParameters.whenTravelReallyBegins,
    BookingSearchParameters.sectorBeginDate,
    BookingSearchParameters.sectorArrivalDate,
    BookingSearchParameters.ticketSectorBeginDate,
    BookingSearchParameters.whenExecutedOperation,
    BookingSearchParameters.stayBeginDate,
    BookingSearchParameters.stayArrivalDate,
];

@Injectable()
export class ConfigFieldBuilderService {
    constructor(
        private readonly helperService: HelperService
    ) {
    }

    public buildFieldConfigCheckbox(form: FormGroup, fields: BookingSearchParameters[]): IFieldConfig {
        return fields.reduce((config: IFieldConfig, name: BookingSearchParameters) => {
            return {
                ...config,
                [name]: this.helperService.getCheckboxFieldOptions(
                    name,
                    form,
                    FIELD_LABELS,
                    FIELD_DESCRIPTION_LABELS,
                    FIELD_CHECKBOX_LABELS
                ),
            };
        }, {});
    }

    public buildFieldConfigArray(form: FormGroup, fields: BookingSearchParameters[]): IFieldConfig {
        return fields.reduce((config: IFieldConfig, name: BookingSearchParameters) => {
            return {
                ...config,
                [name]: this.helperService.getSimpleFieldOptions(
                    name,
                    form,
                    FIELD_LABELS,
                    FIELD_DESCRIPTION_LABELS
                ),
            };
        }, {});
    }

    public buildFieldConfigDate(form: FormGroup, fields: string[]): IFieldConfig {
        return fields.reduce((config: IFieldConfig, name: string): IFieldConfig => {
            return {
                ...config,
                [`${name}Missing`]: this.getMissingDateCheckboxFieldSpec(form, 'missing', name),
                [`${name}Min`]: this.getDateFieldSpec(form, 'min', name, this.getValidators(form)[`${name}Min`]),
                [`${name}Max`]: this.getDateFieldSpec(form, 'max', name, this.getValidators(form)[`${name}Max`]),
            };
        }, {});
    }

    public buildFieldConfigNumber(form: FormGroup, fields: string[]): IFieldConfig {
        const output: IFieldConfig =
         fields.reduce((config: IFieldConfig, name: BookingSearchParameters): IFieldConfig => {
            return {
                ...config,
                [name]: this.helperService.getNumberFieldOptions(
                    name,
                    form,
                    FIELD_LABELS,
                    name.includes('Percentage') ? 0: 2,
                    name.includes('Percentage') ? 100: null,
                 FIELD_DESCRIPTION_LABELS
                ),
            };
        }, {});
        return output;
    }

    public buildFieldConfigCode(form: FormGroup, fields: string[]): IFieldConfig {
        return fields.reduce((config: IFieldConfig, name: string) => {
            return {
                ...config,
                [name]: this.getTextCodeFieldOptions(form, name),
            };
        }, {});
    }

    public createDateRangeOptionConfig(fieldName: BookingSearchParameters, fieldConfig: IFieldConfig): IDateRangeOptionConfig {
        const active: boolean = DEFAULT_ACTIVE_DATE_FIELDS.includes(fieldName);

        return {
            label: FIELD_LABELS[fieldName],
            type: OptionConfigTypes.DateRange,
            missingDateFieldConfig: fieldConfig[`${fieldName}Missing`] as ICheckboxFieldSpecs,
            fieldConfig: {
                min: fieldConfig[`${fieldName}Min`],
                max: fieldConfig[`${fieldName}Max`],
            },
            formControl: fieldName,
            active,
        };
    }

    private getValidators(form: FormGroup): { [key: string]: IValidator[] } {
        return validatorConfig(form);
    }

    private getDateFieldSpec(form: FormGroup, controlName: string, groupName: string, validators?: IValidator[]): IDateFieldSpecs {
        const config: IDateFieldConfig = DATE_FIELDS_CONFIG;
        let fieldSpecs: IDateFieldSpecs =
            this.getSimpleFieldSpec(form, controlName, validators, groupName);
        const today: Date = new Date();
        const dateFields: Partial<IDateFieldSpecs> = {
            ...(config[groupName].minDateOffset || config[groupName].minDateOffset === 0 ?
                {
                    minDate: this.getOffsetDateFromToday(config[groupName].minDateOffset, 'past')
                } : null),
            ...(config[groupName].maxDateOffset || config[groupName].maxDateOffset === 0 ?
                {
                    maxDate: this.getOffsetDateFromToday(config[groupName].maxDateOffset, 'future')
                } : null),
            defaultDate: today,
            icon: 'fa fa-calendar'
        };
        fieldSpecs = {...fieldSpecs, ...dateFields};

        return fieldSpecs;
    }

    private getNumberFieldSpec(form: FormGroup, controlName: string, groupName: string, validators?: IValidator[]): IDateFieldSpecs {
        let fieldSpecs: INumberFieldSpecs =
            this.getSimpleFieldSpec(form, controlName, validators, groupName);

        fieldSpecs = {...fieldSpecs, min: 0, step: 10};

        return fieldSpecs;
    }

    private getMissingDateCheckboxFieldSpec(formGroup: FormGroup, controlName: string, groupName?: string): ICheckboxFieldSpecs {
        const simpleFieldSpec: IFieldSpecs = this.getSimpleFieldSpec(formGroup, controlName, null, groupName);
        return simpleFieldSpec ? {
            ...simpleFieldSpec,
            checkboxLabel: TEMPLATE_LABELS.missingDateField,
        } : null;
    }

    private getSimpleFieldSpec(form: FormGroup, controlName: string, validators?: IValidator[], groupName?: string): IFieldSpecs {

        const fieldName: string = groupName ? groupName + _upperFirst(controlName) : controlName;
        const formGroup: FormGroup = groupName ? form.get(groupName) as FormGroup : form;
        if (!FIELD_LABELS[fieldName]) {
            return null;
        }

        return {
            formGroup,
            label: FIELD_LABELS[fieldName],
            formControlName: controlName,
            validators,
            description: FIELD_DESCRIPTION_LABELS[fieldName] || null,
        };
    }

    private getOffsetDateFromToday(offsetValue: number, offsetDirection: string): Date {
        if (offsetValue === 0) {
            return new Date();
        }
        const dateOffset: moment.Moment = offsetDirection === 'past' ?
            moment().subtract(offsetValue, 'days') :
            moment().add(offsetValue, 'days');
        return offsetValue === null ? null : dateOffset.toDate();
    }

    private getTextCodeFieldOptions(form: FormGroup, groupName: string, validators?: IValidator[]): IFieldSpecs {
        const formGroup: FormGroup = form.get(groupName) as FormGroup;
        // FIXME : should evolve when their'll be search for this field
        // currently force the filling of the 'text' control
        const controlName: string = 'text';

        return {
            formGroup,
            label: FIELD_LABELS[groupName],
            formControlName: controlName,
            validators,
            description: FIELD_DESCRIPTION_LABELS[groupName] || null,
        };
    }
}
